import { useNavigate, Link } from 'react-router-dom';
import { openUrl } from '../lib/capacitor';
import { trpc } from '../lib/trpc';
import PersonaAvatar from '../components/PersonaAvatar';

const FEATURED_SLUG = 'simone-weil';

export default function Landing() {
  const navigate = useNavigate();
  const { data: personas } = trpc.persona.list.useQuery();

  // Pin the featured persona first, then fill with 2 others
  const featured = personas
    ? [
        ...(personas.filter((p) => p.slug === FEATURED_SLUG)),
        ...personas.filter((p) => p.slug !== FEATURED_SLUG).slice(0, 2),
      ].slice(0, 3)
    : [];

  return (
    <div className="min-h-dvh bg-dark flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4">
        <h1 className="font-serif text-xl text-gold font-bold tracking-wide">Satguru</h1>
        <button
          onClick={() => navigate('/auth')}
          className="text-sm text-sage-300 hover:text-gold transition-colors font-medium"
        >
          Sign In
        </button>
      </header>

      {/* Hero */}
      <section className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
        <p className="text-xs uppercase tracking-[0.25em] text-gold/60 font-semibold mb-4">
          Direction from above
        </p>
        <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-100 leading-tight max-w-md mb-4">
          Converse with history's greatest minds
        </h2>
        <p className="text-sage-300 max-w-sm mb-8 leading-relaxed">
          AI-powered conversations grounded in real texts. Sit with Simone
          Weil's attention, descend into Jung's shadow, or rest in the silence
          of Ajahn Chah's forest.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
          <button
            onClick={() => navigate('/try/carl-jung?msg=What+is+the+shadow%2C+and+how+do+I+face+mine%3F')}
            className="btn-primary flex-1"
          >
            Try Free
          </button>
          <button
            onClick={() => navigate('/auth?mode=signup')}
            className="btn-secondary flex-1"
          >
            Create Account
          </button>
        </div>
      </section>

      {/* Featured personas */}
      {featured.length > 0 && (
        <section className="px-6 pb-12">
          <h3 className="text-xs uppercase tracking-[0.2em] text-sage-400 font-semibold mb-4 text-center">
            Meet the thinkers
          </h3>
          <div className="flex flex-col gap-3 max-w-md mx-auto">
            {featured.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/explore/${p.slug}`)}
                className="card text-left hover:border-sage-600 transition-colors flex items-center gap-4"
              >
                <PersonaAvatar name={p.name} color={p.color} size="lg" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-100">{p.name}</h4>
                  {p.era && <p className="text-xs text-sage-400">{p.era}</p>}
                  {p.tagline && (
                    <p className="text-sm text-gray-400 mt-0.5 line-clamp-1">{p.tagline}</p>
                  )}
                </div>
                <span className="text-sage-500 text-sm">→</span>
              </button>
            ))}
          </div>
          <p className="text-center mt-4">
            <button
              onClick={() => navigate('/explore')}
              className="text-sm text-gold hover:text-gold-300 transition-colors font-medium"
            >
              View all personas →
            </button>
          </p>
        </section>
      )}

      {/* Value props */}
      <section className="px-6 pb-12">
        <div className="max-w-md mx-auto space-y-6">
          <div className="flex gap-4 items-start">
            <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
              <span className="text-gold text-lg">📚</span>
            </div>
            <div>
              <h4 className="font-semibold text-gray-100 text-sm">Grounded in real texts</h4>
              <p className="text-sage-400 text-sm mt-0.5">
                Every response draws from thousands of indexed passages from primary sources.
              </p>
            </div>
          </div>
          <div className="flex gap-4 items-start">
            <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
              <span className="text-gold text-lg">🧠</span>
            </div>
            <div>
              <h4 className="font-semibold text-gray-100 text-sm">Memory that deepens</h4>
              <p className="text-sage-400 text-sm mt-0.5">
                Your conversations build context over time. The thinkers remember
                you.
              </p>
            </div>
          </div>
          <div className="flex gap-4 items-start">
            <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
              <span className="text-gold text-lg">✨</span>
            </div>
            <div>
              <h4 className="font-semibold text-gray-100 text-sm">Cross-persona discovery</h4>
              <p className="text-sage-400 text-sm mt-0.5">
                Exploring the Shadow? The system suggests Nietzsche. Following
                threads across minds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="px-6 pb-12 text-center">
        <div className="max-w-md mx-auto card bg-gradient-to-b from-dark-50 to-dark-100 border-sage-700">
          <p className="text-xs uppercase tracking-[0.2em] text-gold/60 font-semibold mb-2">
            Start free, go deeper
          </p>
          <p className="text-sage-300 text-sm mb-4">
            Free tier includes daily messages. Premium unlocks 1,000
            messages/month, conversation memory, and personalization.
          </p>
          <button
            onClick={() => openUrl('/pricing', navigate)}
            className="text-sm text-gold hover:text-gold-300 transition-colors font-medium"
          >
            View pricing →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-6 border-t border-sage-800 text-center space-y-2">
        <p className="text-xs text-sage-500">
          Satguru — wisdom from history's greatest minds
        </p>
        <p className="text-xs text-sage-600">
          <Link to="/terms" className="hover:text-sage-400 transition-colors">Terms</Link>
          {' · '}
          <Link to="/privacy" className="hover:text-sage-400 transition-colors">Privacy</Link>
        </p>
      </footer>
    </div>
  );
}
